import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customFetcher } from '../useFetch';
import { IProcessManagerType } from '../../Interfaces/IProcessManagerType';
import { IProcessManagerLandRegister } from '../../Interfaces/IProcessManager';
import { IProcessManagerAutomationType } from '../../Interfaces/IProcessManagerAutomationType';

export const useGetProcessManagerTypes = () => {
  const { data: processManagerTypes, isLoading: isLoadingManagerTypes } =
    useQuery<IProcessManagerType[]>({
      queryKey: ['processmanagertype'],
      queryFn: () => customFetcher({ endpoint: '/processmanagertype' }),
    });

  return { processManagerTypes, isLoadingManagerTypes };
};

export const useGetProcessManagerTracking = () => {
  const {
    data: processManagerTrackings,
    isLoading: isLoadingManagerTrackings,
    refetch: refetchManagerTrackings,
  } = useQuery<IProcessManagerLandRegister[]>({
    queryKey: ['processmanager/tracking'],
    queryFn: () => customFetcher({ endpoint: '/processmanager/tracking' }),
  });

  return {
    processManagerTrackings,
    isLoadingManagerTrackings,
    refetchManagerTrackings,
  };
};

/**
 * Bulk-Aktion für Nachverfolgungen: mehrere auf einmal deaktivieren
 * (`action: 'deactivate'` → schließen) oder löschen (`action: 'delete'` →
 * Soft-Delete). Backend: PUT /processmanager/tracking/bulk. Nach Erfolg wird
 * die Trackings-Liste invalidiert.
 */
export const useBulkTrackingAction = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation<
    { action: string; requested: number },
    Error,
    { ids: number[]; action: 'deactivate' | 'delete' }
  >({
    mutationFn: ({ ids, action }) =>
      customFetcher({
        endpoint: '/processmanager/tracking/bulk',
        method: 'PUT',
        body: { ids, action },
      }) as Promise<{ action: string; requested: number }>,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['processmanager/tracking'],
      });
    },
  });
  return {
    bulkTrackingAction: mutateAsync,
    isBulkTrackingActionPending: isPending,
  };
};

export const useGetProcessManagerAutomationTypes = () => {
  const {
    data: processManagerAutomationTypes,
    isLoading: isLoadingManagerAutomationTypes,
  } = useQuery<IProcessManagerAutomationType[]>({
    queryKey: ['processmanagerautomationtype'],
    queryFn: () => customFetcher({ endpoint: '/processmanagerautomationtype' }),
  });

  return { processManagerAutomationTypes, isLoadingManagerAutomationTypes };
};
